import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, CLOUDINARY_CLOUD_NAME } from '../../config/constants';
import { auth } from '../login/FirebaseConfig';
import ConfirmationModal from '../Modal';
import '../../css/TablaAbmEventos.css';

function TablaAbmEventos() {
    const [documentData, setDocumentData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);

    const [eventStates, setEventStates] = useState({});
    const [loadingToggle, setLoadingToggle] = useState({});
    const [allButtonsEnabled, setAllButtonsEnabled] = useState(true);

    const navigate = useNavigate();
    const COLLECTION_EVENTOS_NAME = 'eventos';
    const user = auth.currentUser;

    useEffect(() => {
        const fetchDocumentDetails = async () => {
            try {
                const idToken = await user.getIdToken();

                const response = await axios.post(`${API_BASE_URL}/getDocumentos`, { collectionName: COLLECTION_EVENTOS_NAME },
                    {
                        headers: {
                            Authorization: `Bearer ${idToken}`,
                        },
                    }
                );

                const docIds = response.data.doc_ids;

                const documentPromises = docIds.map(async (docId) => {
                    const docResponse = await axios.post(
                        `${API_BASE_URL}/getInfoDocumento`,
                        { docId, collectionName: COLLECTION_EVENTOS_NAME },
                        {
                            headers: {
                                Authorization: `Bearer ${idToken}`,
                            },
                        }
                    );
                    return docResponse.data.doc_info;
                });

                const documents = await Promise.all(documentPromises);

                const fetchedEventStates = {};
                documents.forEach((doc) => {
                    fetchedEventStates[doc.id] = doc.estado || 'desactivado';
                });

                setDocumentData(documents);
                setEventStates(fetchedEventStates);
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.error || 'Error recuperando información');
                setLoading(false);
            }
        };

        fetchDocumentDetails();
    }, []);

    const handleDelete = async () => {
        if (!eventToDelete) return;
        setShowModal(false);
        setDeleting(true);
        try {
            const idToken = await user.getIdToken();
            await axios.delete(`${API_BASE_URL}/eliminarArchivo/${imageToDelete}`, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            await axios.delete(`${API_BASE_URL}/eliminarDocumento/${COLLECTION_EVENTOS_NAME}/${eventToDelete}`, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            setDocumentData((prevData) =>
                prevData.filter((doc) => doc.id !== eventToDelete)
            );
            setEventToDelete(null);
            setImageToDelete(null);
        } catch (err) {
            console.error('Error eliminando evento:', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleShowModal = (eventId, imagePublicId) => {
        setEventToDelete(eventId);
        setImageToDelete(imagePublicId);
        setShowModal(true);
    };

    const handleCancelDelete = () => {
        setEventToDelete(null);
        setImageToDelete(null);
        setShowModal(false);
    };

    const handleEditar = async (docid) => {
        navigate('/editarEvento', { state: { docid } });
    };

    const handleActivarDesactivarEvento = async (docId) => {
        setLoadingToggle((prev) => ({ ...prev, [docId]: true }));

        const currentState = eventStates[docId];
        setAllButtonsEnabled(false);

        try {
            const idToken = await user.getIdToken();
            const endpoint = currentState === 'activado' ? '/desactivarEvento' : '/activarEvento';
            await axios.post(`${API_BASE_URL}${endpoint}`, { docId }, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            setEventStates((prev) => ({
                ...prev,
                [docId]: currentState === 'activado' ? 'desactivado' : 'activado',
            }));
        } catch (error) {
            console.error(`Error actualizando el estado para ${docId}:`, error);
        } finally {
            setLoadingToggle((prev) => ({ ...prev, [docId]: false }));
            setAllButtonsEnabled(true);
        }
    };

    const getImageUrl = (publicId) => {
        return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/f_auto/${publicId}`;
    };

    const convertDate = (timestamp) => {
        const date = new Date(timestamp._seconds * 1000 + timestamp._nanoseconds / 1e6);
        const formatter = new Intl.DateTimeFormat('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        const parts = formatter.formatToParts(date);
        const formattedDate = `${parts.find((p) => p.type === 'day').value} ${parts.find((p) => p.type === 'month').value.toUpperCase()}
        ${parts.find((p) => p.type === 'year').value} ${parts.find((p) => p.type === 'hour').value}:${parts.find((p) => p.type === 'minute').value}`;

        return formattedDate;
    };


    if (error) return <p>Error: {error}</p>;

    return (
        <div className="container mt-4 divfill">
            <h2 className="mb-4">Administrador de Eventos</h2>
            <div className="table-responsive">
                <table className="table table-striped table-bordered">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Descripción</th>
                            <th style={{ width: '40%' }}>Imagen</th>
                            <th style={{ width: '9%' }}>Fecha del evento</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documentData
                            .slice()
                            .sort((a, b) => new Date(a.eventDate._seconds * 1000 + a.eventDate._nanoseconds / 1e6) -
                                new Date(b.eventDate._seconds * 1000 + b.eventDate._nanoseconds / 1e6)) // Ordena ascendentemente por fecha del evento
                            .map((doc) => (
                                <tr key={doc.id}>
                                    <td>{doc.nombre || 'N/A'}</td>
                                    <td>
                                        <div className="scrollable-cell">{doc.descripcion || 'N/A'}</div>
                                    </td>
                                    <td>
                                        <a href={getImageUrl(doc.imagePublicId)} target="_blank" rel="noopener noreferrer">
                                            <img src={getImageUrl(doc.imagePublicId)} alt={`Imagen ${doc.nombre}`} style={{ maxWidth: '75%' }} />
                                        </a>
                                    </td>
                                    <td>{convertDate(doc.eventDate) || 'N/A'}</td>
                                    <td>
                                        <button className="btn btn-warning" onClick={() => handleEditar(doc.id)}>Editar</button>
                                        <br />
                                        <button
                                            className="btn btn-danger mt-1"
                                            onClick={() => handleShowModal(doc.id, doc.imagePublicId)}
                                            disabled={deleting}
                                        >
                                            {deleting && eventToDelete === doc.id ? (
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                            ) : (
                                                'Eliminar'
                                            )}
                                        </button>
                                        <br />
                                        <button
                                            className={`btn ${eventStates[doc.id] === 'activado' ? 'btn-success' : 'btn-secondary'} mt-1`}
                                            onClick={() => handleActivarDesactivarEvento(doc.id)}
                                            disabled={loadingToggle[doc.id] || !allButtonsEnabled}
                                        >
                                            {loadingToggle[doc.id] ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                eventStates[doc.id] === 'activado' ? 'Activado' : 'Desactivado'
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <button
                className="btn btn-success mt-2 mb-4"
                onClick={() => navigate('/crearEvento')}
            >
                Crear Nuevo Evento
            </button>

            <ConfirmationModal
                show={showModal}
                onConfirm={handleDelete}
                onCancel={handleCancelDelete}
                message="¿Estás seguro de que deseas eliminar este evento? Esta acción es IRREVERSIBLE."
            />
        </div>
    );
}

export default TablaAbmEventos;
